import * as THREE from 'three';
import { TILE_SIZE } from '../labyrinth/Labyrinth';
import type { Enemy, LootChest } from '../labyrinth/Entity';
import { applyStylizedShading, type LightmapBindings } from './StylizedShading';

// Stylized shading config shared by every enemy + loot material. Rim + AO +
// half-Lambert give the primitive rigs visible form against the map's lightmap.
const ENEMY_SHADING = {
  lightmapNormalOffset: false,
  keyLightStrength: 0.28,
  rimStrength: 0.50,
  rimPower: 2.4,
  verticalAOStrength: 0.22,
} as const;

// Glow / aura materials (bomber cap, charger glow plate, shielder bubble) want
// their own stylization: no AO (self-lit look) and a hotter rim.
const GLOW_SHADING = {
  lightmapNormalOffset: false,
  keyLightStrength: 0.10,
  rimStrength: 0.80,
  rimPower: 2.0,
  verticalAOStrength: 0.0,
} as const;

// All rigs render with MeshBasicMaterial — the scene has no Three.js lights
// (see LabyrinthMesh), so HP/state are expressed via color tint rather than
// emissive. Angle convention: robot-style, forward = (cos(angle), sin(angle))
// in world (X, Z) — so Three.js rotation.y = -angle.

const BASE = TILE_SIZE * 0.32;
const BOSS_SCALE = 1.5;

// Per-kind base colors (pulled loosely from EnemyGraphic hp-bar colors).
const KIND_COLOR: Record<Enemy['enemyKind'], number> = {
  turret:   0x6a2820,
  patrol:   0x5a1430,
  charger:  0x804020,
  bomber:   0x7a5a18,
  shielder: 0x225a78,
};

const BOSS_ACCENT = 0xffcc00;

export interface EnemyRig {
  readonly root: THREE.Group;
  update(entity: Enemy, t: number): void;
  dispose(): void;
}

export interface LootRig {
  readonly root: THREE.Group;
  update(loot: LootChest, t: number): void;
  dispose(): void;
}

/** Lerp mesh color toward red as HP drops. */
function applyHpTint(mat: THREE.MeshBasicMaterial, baseColor: number, hp: number, maxHp: number): void {
  const frac = Math.max(0, Math.min(1, hp / Math.max(1, maxHp)));
  const br = ((baseColor >> 16) & 0xff) / 255;
  const bg = ((baseColor >>  8) & 0xff) / 255;
  const bb =  (baseColor        & 0xff) / 255;
  // At 0 HP, pull 70% toward dark red (0x66, 0x11, 0x11) to make damage visible.
  const mix = 1 - frac;
  const r = br * (1 - mix * 0.7) + 0.40 * mix * 0.7;
  const g = bg * (1 - mix * 0.7) + 0.07 * mix * 0.7;
  const b = bb * (1 - mix * 0.7) + 0.07 * mix * 0.7;
  mat.color.setRGB(r, g, b);
}

// ─── Turret enemy ───────────────────────────────────────────
function createTurretRig(lightmap: LightmapBindings): EnemyRig {
  const root = new THREE.Group();
  const s = BASE;

  const baseMat = new THREE.MeshBasicMaterial({ color: KIND_COLOR.turret, transparent: true });
  const baseGeo = new THREE.CylinderGeometry(s, s * 1.1, s * 0.6, 16);
  const base = new THREE.Mesh(baseGeo, baseMat);
  base.position.y = s * 0.3;
  root.add(base);

  const barrelPivot = new THREE.Group();
  barrelPivot.position.y = s * 0.7;
  root.add(barrelPivot);

  const barrelMat = new THREE.MeshBasicMaterial({ color: 0x2a1a14, transparent: true });
  const barrelGeo = new THREE.BoxGeometry(s * 1.4, s * 0.3, s * 0.35);
  const barrel = new THREE.Mesh(barrelGeo, barrelMat);
  barrel.position.x = s * 0.5;
  barrelPivot.add(barrel);

  applyStylizedShading(baseMat,   { lightmap, ...ENEMY_SHADING });
  applyStylizedShading(barrelMat, { lightmap, ...ENEMY_SHADING });

  return {
    root,
    update(e, _t) {
      const scale = e.isBoss ? BOSS_SCALE : 1;
      root.scale.setScalar(scale);
      barrelPivot.rotation.y = -e.angle;
      applyHpTint(baseMat, e.isBoss ? BOSS_ACCENT : KIND_COLOR.turret, e.hp, e.maxHp);
      baseMat.opacity = e.visAlpha;
      barrelMat.opacity = e.visAlpha;
    },
    dispose() {
      baseGeo.dispose(); barrelGeo.dispose();
      baseMat.dispose(); barrelMat.dispose();
    },
  };
}

// ─── Patrol enemy ───────────────────────────────────────────
function createPatrolRig(lightmap: LightmapBindings): EnemyRig {
  const root = new THREE.Group();
  const s = BASE;

  const bodyMat = new THREE.MeshBasicMaterial({ color: KIND_COLOR.patrol, transparent: true });
  const bodyGeo = new THREE.BoxGeometry(s * 1.6, s * 0.9, s * 1.3);
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = s * 0.55;
  root.add(body);

  // Forward-facing sensor protrusion.
  const sensorMat = new THREE.MeshBasicMaterial({ color: 0xff88aa, transparent: true });
  const sensorGeo = new THREE.BoxGeometry(s * 0.4, s * 0.25, s * 0.4);
  const sensor = new THREE.Mesh(sensorGeo, sensorMat);
  sensor.position.set(s * 0.9, s * 0.9, 0);
  root.add(sensor);

  applyStylizedShading(bodyMat,   { lightmap, ...ENEMY_SHADING });
  applyStylizedShading(sensorMat, { lightmap, ...GLOW_SHADING });

  return {
    root,
    update(e, _t) {
      const scale = e.isBoss ? BOSS_SCALE : 1;
      root.scale.setScalar(scale);
      root.rotation.y = -e.angle;
      applyHpTint(bodyMat, e.isBoss ? BOSS_ACCENT : KIND_COLOR.patrol, e.hp, e.maxHp);
      bodyMat.opacity = e.visAlpha;
      sensorMat.opacity = e.visAlpha;
    },
    dispose() {
      bodyGeo.dispose(); sensorGeo.dispose();
      bodyMat.dispose(); sensorMat.dispose();
    },
  };
}

// ─── Charger enemy ──────────────────────────────────────────
function createChargerRig(lightmap: LightmapBindings): EnemyRig {
  const root = new THREE.Group();
  const s = BASE;

  const bodyMat = new THREE.MeshBasicMaterial({ color: KIND_COLOR.charger, transparent: true });
  const bodyGeo = new THREE.BoxGeometry(s * 2.0, s * 0.8, s * 1.1); // elongated forward
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = s * 0.5;
  root.add(body);

  // Forward glow plate — intensifies (brightens) during charge states.
  const glowMat = new THREE.MeshBasicMaterial({ color: 0x441100, transparent: true });
  const glowGeo = new THREE.BoxGeometry(s * 0.3, s * 0.55, s * 0.9);
  const glow = new THREE.Mesh(glowGeo, glowMat);
  glow.position.set(s * 1.05, s * 0.5, 0);
  root.add(glow);

  applyStylizedShading(bodyMat, { lightmap, ...ENEMY_SHADING });
  applyStylizedShading(glowMat, { lightmap, ...GLOW_SHADING });

  return {
    root,
    update(e, t) {
      const scale = e.isBoss ? BOSS_SCALE : 1;
      root.scale.setScalar(scale);
      root.rotation.y = -e.angle;
      applyHpTint(bodyMat, e.isBoss ? BOSS_ACCENT : KIND_COLOR.charger, e.hp, e.maxHp);
      bodyMat.opacity = e.visAlpha;

      const state = e.enemyKind === 'charger' ? e.chargeState : 'idle';
      const base = 0x221100;
      const peak = 0xff6622;
      const pulse = 0.5 + 0.5 * Math.sin(t * 10);
      let mix = 0;
      if (state === 'charging') mix = 0.6 + pulse * 0.4;
      else if (state === 'recovering') mix = 0.2;
      const r = ((base >> 16) & 0xff) * (1 - mix) + ((peak >> 16) & 0xff) * mix;
      const g = ((base >>  8) & 0xff) * (1 - mix) + ((peak >>  8) & 0xff) * mix;
      const b =  (base        & 0xff) * (1 - mix) + ( peak        & 0xff) * mix;
      glowMat.color.setRGB(r / 255, g / 255, b / 255);
      glowMat.opacity = e.visAlpha;
    },
    dispose() {
      bodyGeo.dispose(); glowGeo.dispose();
      bodyMat.dispose(); glowMat.dispose();
    },
  };
}

// ─── Bomber enemy ───────────────────────────────────────────
function createBomberRig(lightmap: LightmapBindings): EnemyRig {
  const root = new THREE.Group();
  const s = BASE;

  const bodyMat = new THREE.MeshBasicMaterial({ color: KIND_COLOR.bomber, transparent: true });
  const bodyGeo = new THREE.SphereGeometry(s * 0.9, 20, 14);
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = s * 0.9;
  root.add(body);

  // Small "fuse" cap that pulses as the attack timer approaches fire.
  const capMat = new THREE.MeshBasicMaterial({ color: 0x552200, transparent: true });
  const capGeo = new THREE.SphereGeometry(s * 0.22, 10, 8);
  const cap = new THREE.Mesh(capGeo, capMat);
  cap.position.set(0, s * 1.8, 0);
  root.add(cap);

  applyStylizedShading(bodyMat, { lightmap, ...ENEMY_SHADING });
  applyStylizedShading(capMat,  { lightmap, ...GLOW_SHADING });

  return {
    root,
    update(e, t) {
      const scale = e.isBoss ? BOSS_SCALE : 1;
      root.scale.setScalar(scale);
      root.rotation.y = -e.angle;
      applyHpTint(bodyMat, e.isBoss ? BOSS_ACCENT : KIND_COLOR.bomber, e.hp, e.maxHp);
      bodyMat.opacity = e.visAlpha;

      // Pulse brightness when cooldown is near ready (attackTimer >= 0.7 * 1/attackSpeed).
      const cycle = 1 / Math.max(0.1, e.attackSpeed);
      const readyFrac = Math.min(1, e.attackTimer / cycle);
      const pulse = 0.5 + 0.5 * Math.sin(t * 12);
      const tint = 0.3 + readyFrac * 0.7 * (0.5 + pulse * 0.5);
      capMat.color.setRGB(0.3 + tint * 0.7, 0.1 + tint * 0.3, 0.05);
      capMat.opacity = e.visAlpha;
    },
    dispose() {
      bodyGeo.dispose(); capGeo.dispose();
      bodyMat.dispose(); capMat.dispose();
    },
  };
}

// ─── Shielder enemy ─────────────────────────────────────────
function createShielderRig(lightmap: LightmapBindings): EnemyRig {
  const root = new THREE.Group();
  const s = BASE;

  const bodyMat = new THREE.MeshBasicMaterial({ color: KIND_COLOR.shielder, transparent: true });
  const bodyGeo = new THREE.BoxGeometry(s * 1.4, s * 1.1, s * 1.4);
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = s * 0.7;
  root.add(body);

  const shieldMat = new THREE.MeshBasicMaterial({
    color: 0x66ccff,
    transparent: true,
    opacity: 0.18,
    depthWrite: false,
  });
  const shieldGeo = new THREE.SphereGeometry(1, 20, 14); // unit sphere; scale to auraRadius
  const shield = new THREE.Mesh(shieldGeo, shieldMat);
  shield.position.y = s * 0.7;
  root.add(shield);

  applyStylizedShading(bodyMat, { lightmap, ...ENEMY_SHADING });
  // Shield aura is its own look — fresnel gives it a proper hologram rim without
  // any lightmap darkening (we want it glowing even in pitch-black rooms).
  applyStylizedShading(shieldMat, {
    keyLightStrength: 0, rimStrength: 1.2, rimPower: 1.8, verticalAOStrength: 0,
  });

  return {
    root,
    update(e, _t) {
      const scale = e.isBoss ? BOSS_SCALE : 1;
      root.scale.setScalar(scale);
      root.rotation.y = -e.angle;
      applyHpTint(bodyMat, e.isBoss ? BOSS_ACCENT : KIND_COLOR.shielder, e.hp, e.maxHp);
      bodyMat.opacity = e.visAlpha;

      const radius = (e.enemyKind === 'shielder' ? e.auraRadius : 0) * TILE_SIZE;
      shield.scale.setScalar(Math.max(0.01, radius));
      shieldMat.opacity = 0.18 * e.visAlpha;
    },
    dispose() {
      bodyGeo.dispose(); shieldGeo.dispose();
      bodyMat.dispose(); shieldMat.dispose();
    },
  };
}

// ─── Factory ────────────────────────────────────────────────
export function createEnemyRig(entity: Enemy, lightmap: LightmapBindings): EnemyRig {
  switch (entity.enemyKind) {
    case 'turret':   return createTurretRig(lightmap);
    case 'patrol':   return createPatrolRig(lightmap);
    case 'charger':  return createChargerRig(lightmap);
    case 'bomber':   return createBomberRig(lightmap);
    case 'shielder': return createShielderRig(lightmap);
  }
}

// ─── Loot rig ───────────────────────────────────────────────
export function createLootRig(lightmap: LightmapBindings): LootRig {
  const root = new THREE.Group();
  const s = TILE_SIZE * 0.28;

  const mat = new THREE.MeshBasicMaterial({ color: 0xddaa22, transparent: true });
  const geo = new THREE.BoxGeometry(s * 1.8, s * 1.1, s * 1.2);
  const mesh = new THREE.Mesh(geo, mat);
  mesh.position.y = s * 0.8;
  root.add(mesh);

  // Gold rim for visibility.
  const rimMat = new THREE.MeshBasicMaterial({ color: 0xffee66, transparent: true });
  const rimGeo = new THREE.BoxGeometry(s * 1.9, s * 0.15, s * 1.3);
  const rim = new THREE.Mesh(rimGeo, rimMat);
  rim.position.y = s * 1.35;
  root.add(rim);

  applyStylizedShading(mat,    { lightmap, ...ENEMY_SHADING });
  applyStylizedShading(rimMat, { lightmap, ...GLOW_SHADING });

  return {
    root,
    update(loot, t) {
      // Gentle bob.
      const bob = Math.sin(t * 2) * s * 0.12;
      root.position.y = bob;
      mat.opacity = loot.visAlpha;
      rimMat.opacity = loot.visAlpha;
    },
    dispose() {
      geo.dispose(); rimGeo.dispose();
      mat.dispose(); rimMat.dispose();
    },
  };
}
